import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { errorMessage } from "helpers/common";
import { fetchPeriods, getActivePeriod, getPeriods } from "store/periodSlice";

const getPeriodRisk = (periods = []) => {
  const totalPeriods = Array.isArray(periods) ? periods.length : 0;

  return {
    periodCount: totalPeriods,
    shouldWarn: totalPeriods > 1,
  };
};

export default function useImportPeriodGuard(setError) {
  const dispatch = useDispatch();
  const periods = useSelector(getPeriods);
  const activePeriod = useSelector(getActivePeriod);

  const pendingActionRef = useRef(null);

  const [checkingPeriods, setCheckingPeriods] = useState(false);
  const [warningState, setWarningState] = useState({
    activePeriodName: '',
    draftCount: 0,
    liveCount: 0,
    show: false,
  });

  const runWithPeriodGuard = useCallback(async (action) => {
    setCheckingPeriods(true);

    try {
      let resolvedPeriods = periods;
      let resolvedActivePeriod = activePeriod;

      if (!Array.isArray(resolvedPeriods)) {
        const result = await dispatch(fetchPeriods());

        if (!fetchPeriods.fulfilled.match(result)) {
          throw result?.payload || new Error('Unable to fetch periods');
        }

        resolvedPeriods = result.payload?.data?.periods || [];
        resolvedActivePeriod = result.payload?.data?.active_period || resolvedActivePeriod;
      }

      const risk = getPeriodRisk(resolvedPeriods);

      if (risk.shouldWarn) {
        pendingActionRef.current = action;
        setWarningState({
          activePeriodName: resolvedActivePeriod || '',
          draftCount: 0,
          liveCount: risk.periodCount,
          show: true,
        });

        return false;
      }

      await action();
      return true;
    } catch (err) {
      if (typeof setError === 'function') {
        setError(errorMessage(err, 'check import periods'));
      }

      return false;
    } finally {
      setCheckingPeriods(false);
    }
  }, [activePeriod, dispatch, periods, setError]);

  const handleCancelPeriodWarning = useCallback(() => {
    pendingActionRef.current = null;
    setWarningState(prev => ({ ...prev, show: false }));
  }, []);

  const handleConfirmPeriodWarning = useCallback(async () => {
    const pendingAction = pendingActionRef.current;

    pendingActionRef.current = null;
    setWarningState(prev => ({ ...prev, show: false }));

    if (!pendingAction) {
      return;
    }

    await pendingAction();
  }, []);

  return {
    checkingPeriods,
    handleCancelPeriodWarning,
    handleConfirmPeriodWarning,
    runWithPeriodGuard,
    warningState,
  };
}
